import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";

/**
 * The sky: daily-bg.png's sunset as a fixed background that the page descends
 * through — now a living one. A raw WebGL fragment shader (no deps) rebuilds
 * the same 7-stop sunset→night gradient, displaced by slow-drifting value
 * noise so the bands breathe, with a faint accent-orange glow at the horizon.
 * Scroll progress slides the visible window from 19:00 orange down into night
 * ink (same mapping as the old CSS background-position trick). No WebGL /
 * shader failure / context loss → the original CSS gradient div. Reduced
 * motion: one static frame, scroll still applies.
 */

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAG = `
precision mediump float;

uniform vec2 u_res;
uniform float u_time;   // seconds, drift speed baked in
uniform float u_scroll; // 0..1 page progress
uniform vec2 u_mouse;   // -1..1, lerped

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.55;
  for (int i = 0; i < 3; i++) {
    v += a * vnoise(p);
    p = p * 2.03 + vec2(17.3, 9.1);
    a *= 0.5;
  }
  return v;
}

// The 7 gradient stops from the old CSS sky (sampled from daily-bg.png).
vec3 sky(float v) {
  vec3 c0 = vec3(0.678, 0.353, 0.424); // #AD5A6C
  vec3 c1 = vec3(0.714, 0.431, 0.337); // #B66E56
  vec3 c2 = vec3(0.482, 0.310, 0.263); // #7B4F43
  vec3 c3 = vec3(0.165, 0.129, 0.157); // #2A2128
  vec3 c4 = vec3(0.082, 0.075, 0.102); // #15131A
  vec3 c5 = vec3(0.071, 0.063, 0.090); // #121017
  vec3 c6 = vec3(0.051, 0.043, 0.071); // #0D0B12
  vec3 c = mix(c0, c1, smoothstep(0.00, 0.14, v));
  c = mix(c, c2, smoothstep(0.14, 0.34, v));
  c = mix(c, c3, smoothstep(0.34, 0.55, v));
  c = mix(c, c4, smoothstep(0.55, 0.72, v));
  c = mix(c, c5, smoothstep(0.72, 0.84, v));
  c = mix(c, c6, smoothstep(0.84, 1.00, v));
  return c;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res; // 0..1, y up
  float aspect = u_res.x / u_res.y;

  // Slow-drifting noise field, gently steered by the cursor.
  float t = u_time * 0.045;
  vec2 np = vec2(uv.x * aspect, uv.y) * 2.2;
  np += u_mouse * 0.3;
  float n = fbm(np + vec2(t * 0.9, t * 0.6));
  float n2 = fbm(np * 1.7 - vec2(t * 0.5, t * 1.1));

  // Same mapping as the old sky: a 600%-tall gradient, scroll slides the window.
  float v = (u_scroll * 5.0 + (1.0 - uv.y)) / 6.0;

  // Bands flow: noise displaces the gradient coordinate, fading to zero in
  // the night zone so the bottom of the page stays calm ink.
  float life = 1.0 - smoothstep(0.30, 0.62, v);
  v += (n - 0.5) * 0.13 * life + (n2 - 0.5) * 0.06 * life;
  v = clamp(v, 0.0, 1.0);

  vec3 col = sky(v);

  // Accent horizon glow (#C06C51) where sunset meets night, breathing.
  float horizon = exp(-pow((v - 0.36) * 9.0, 2.0));
  float pulse = 0.75 + 0.25 * sin(u_time * 0.35);
  col += vec3(0.753, 0.424, 0.318) * horizon * (0.09 + 0.13 * n) * life * pulse;

  // Dither to kill banding on the slow gradients.
  col += (hash(gl_FragCoord.xy) - 0.5) * (1.5 / 255.0);

  gl_FragColor = vec4(col, 1.0);
}
`;

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.error(
      "[noko-sky] shader:",
      gl.getShaderInfoLog(sh),
      "contextLost:",
      gl.isContextLost(),
    );
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

/** The pre-WebGL sky, kept verbatim as the no-WebGL / context-loss fallback. */
function CssSky(): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      el.style.backgroundPositionY = `${progress * 100}%`;
    };
    const onScroll = (): void => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden
      className="fixed inset-0 -z-10"
      style={{
        backgroundImage:
          "linear-gradient(160deg, #AD5A6C 0%, #B66E56 14%, #7B4F43 34%, #2A2128 55%, #15131A 72%, #121017 84%, #0D0B12 100%)",
        backgroundSize: "100% 600%",
        backgroundPositionY: "0%",
        backgroundRepeat: "no-repeat",
      }}
    />
  );
}

export function SkyBackground(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "low-power",
    });
    if (!gl) {
      setFailed(true);
      return undefined;
    }

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const prog = gl.createProgram();
    if (!vs || !fs || !prog) {
      setFailed(true);
      return undefined;
    }
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error(
        "[noko-sky] link:",
        gl.getProgramInfoLog(prog),
        "contextLost:",
        gl.isContextLost(),
      );
      setFailed(true);
      return undefined;
    }
    // gl.useProgram is a WebGL API call, not a React hook — rule false positive.
    // oxlint-disable-next-line
    gl.useProgram(prog);

    // Fullscreen triangle.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, "a_pos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(prog, "u_res");
    const uTime = gl.getUniformLocation(prog, "u_time");
    const uScroll = gl.getUniformLocation(prog, "u_scroll");
    const uMouse = gl.getUniformLocation(prog, "u_mouse");

    const resize = (): void => {
      // DPR capped at 1.5: the sky is a fullscreen fill, 2x+ is wasted fill-rate.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.round(canvas.clientWidth * dpr);
      const h = Math.round(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const scrollProgress = (): number => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      return max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
    };

    // Cursor parallax target, lerped toward in the render loop.
    let mx = 0;
    let my = 0;
    let tx = 0;
    let ty = 0;
    const onPointer = (e: PointerEvent): void => {
      tx = (e.clientX / window.innerWidth) * 2 - 1;
      ty = (e.clientY / window.innerHeight) * 2 - 1;
    };

    let raf = 0;
    const start = performance.now();
    const frame = (): void => {
      raf = 0;
      resize();
      mx += (tx - mx) * 0.04;
      my += (ty - my) * 0.04;
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, (performance.now() - start) / 1000);
      gl.uniform1f(uScroll, scrollProgress());
      gl.uniform2f(uMouse, mx, my);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!reduced) raf = requestAnimationFrame(frame);
    };
    const schedule = (): void => {
      if (!raf && !document.hidden) raf = requestAnimationFrame(frame);
    };

    const onContextLost = (e: Event): void => {
      e.preventDefault();
      setFailed(true);
    };

    canvas.addEventListener("webglcontextlost", onContextLost);
    if (reduced) {
      // Static frame; re-render only when scroll/resize changes the picture.
      frame();
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule);
    } else {
      schedule();
      window.addEventListener("pointermove", onPointer, { passive: true });
      document.addEventListener("visibilitychange", schedule);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("pointermove", onPointer);
      document.removeEventListener("visibilitychange", schedule);
      // Never force loseContext() here: getContext returns the SAME context
      // for the life of the canvas, and StrictMode remounts this effect in
      // dev — a lost context would come back dead on the remount. Just free
      // the GL objects.
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buf);
    };
  }, []);

  if (failed) return <CssSky />;
  return <canvas ref={canvasRef} aria-hidden className="fixed inset-0 -z-10 h-full w-full" />;
}
